/**
 * Pasos del onboarding inteligente.
 * Cada paso: { id, title, render(ctx) → HTMLElement, validate?(data) → null|string }
 *
 * Filosofía: el wizard CONOCE a Carlos. Cada pregunta refuerza el contexto
 * que la IA usará para asistirte mejor cada día.
 */

const h = (tag, attrs = {}, children = []) => {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'class') el.className = v;
    else if (k === 'html') el.innerHTML = v;
    else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2).toLowerCase(), v);
    else if (v != null) el.setAttribute(k, v);
  }
  for (const c of [].concat(children)) {
    if (c == null) continue;
    el.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return el;
};

const intro = (kicker, title, subtitle) => h('div', { class: 'mb-8' }, [
  h('p', { class: 'text-sm text-gold-400 font-medium tracking-widest uppercase' }, kicker),
  h('h2', { class: 'font-display text-3xl md:text-4xl font-bold mt-2' }, title),
  subtitle ? h('p', { class: 'mt-3 text-ink-300 max-w-2xl' }, subtitle) : null,
]);

const input = (label, attrs, hint) => h('div', { class: 'mb-4' }, [
  h('label', { class: 'block text-xs uppercase tracking-widest text-ink-300 mb-2' }, label),
  h('input', { class: 'w-full bg-base-900 border border-base-600 rounded-lg px-4 py-3 text-ink-100 focus:border-gold-400 focus:outline-none transition-colors', ...attrs }),
  hint ? h('p', { class: 'text-xs text-ink-400 mt-1' }, hint) : null,
]);

const textarea = (label, attrs, hint) => h('div', { class: 'mb-4' }, [
  h('label', { class: 'block text-xs uppercase tracking-widest text-ink-300 mb-2' }, label),
  h('textarea', { class: 'w-full bg-base-900 border border-base-600 rounded-lg px-4 py-3 text-ink-100 focus:border-gold-400 focus:outline-none transition-colors min-h-[100px]', ...attrs }),
  hint ? h('p', { class: 'text-xs text-ink-400 mt-1' }, hint) : null,
]);

// ✨ ARREGLO BUG: re-renderizar el componente cuando cambia el estado interno.
// Antes los chips/radio se pintaban una vez y los clicks NO actualizaban el estilo
// (aunque el dato sí se guardaba, el usuario veía siempre la selección inicial).

const chips = (options, initial, onChange) => {
  const container = h('div', { class: 'flex flex-wrap gap-2 mb-2' });
  let current = Array.isArray(initial) ? [...initial] : [];

  function paint() {
    container.innerHTML = '';
    options.forEach((opt) => {
      const isSelected = current.includes(opt);
      const chip = h('button', {
        type: 'button',
        class: `px-3 py-1.5 rounded-full text-sm border transition-all ${
          isSelected
            ? 'bg-gold-400/20 text-gold-200 border-gold-400/50'
            : 'bg-base-700 text-ink-200 border-base-600 hover:border-gold-400/30'
        }`,
        onclick: () => {
          current = isSelected ? current.filter((s) => s !== opt) : [...current, opt];
          paint();           // ← re-renderizar UI con nuevo estado
          onChange(current); // ← propagar al wizard
        },
      }, opt);
      container.appendChild(chip);
    });
  }

  paint();
  return container;
};

const radio = (options, initial, onChange) => {
  const container = h('div', { class: 'grid grid-cols-1 md:grid-cols-2 gap-2 mb-2' });
  let current = initial;

  function paint() {
    container.innerHTML = '';
    options.forEach((opt) => {
      const val = typeof opt === 'string' ? opt : opt.value;
      const lbl = typeof opt === 'string' ? opt : opt.label;
      const desc = typeof opt === 'string' ? null : opt.description;
      const isSelected = current === val;
      const card = h('button', {
        type: 'button',
        class: `text-left p-3 rounded-lg border transition-all ${
          isSelected ? 'bg-gold-400/10 border-gold-400/50' : 'bg-base-800 border-base-600 hover:border-gold-400/30'
        }`,
        onclick: () => {
          current = val;
          paint();
          onChange(val);
        },
      }, [
        h('div', { class: 'font-medium' }, lbl),
        desc ? h('div', { class: 'text-xs text-ink-300 mt-0.5' }, desc) : null,
      ]);
      container.appendChild(card);
    });
  }

  paint();
  return container;
};

export const STEPS = [
  // ============ IDENTITY ============
  {
    id: 'identity',
    title: 'Tu identidad',
    render({ data, user, onChange }) {
      const state = { ...data };
      state.fullName ??= user.fullName ?? '';
      state.nickname ??= user.nickname ?? '';
      state.city ??= user.city ?? 'Alicante';
      state.birthDate ??= '';
      state.pronouns ??= '';
      onChange(state);

      const wrap = h('div', {}, [
        intro('Empecemos', `Bienvenido a CARLOS OS`, 'Lo primero: que el sistema sepa quién eres. Todo esto es 100% privado, vive solo en tu servidor.'),
        input('Nombre completo', { value: state.fullName, oninput: (e) => { state.fullName = e.target.value; onChange(state); }, placeholder: 'Carlos García' }),
        input('Apodo / cómo te llaman', { value: state.nickname, oninput: (e) => { state.nickname = e.target.value; onChange(state); }, placeholder: 'Carlos' }, 'El sistema usará este nombre para hablarte.'),
        input('Ciudad', { value: state.city, oninput: (e) => { state.city = e.target.value; onChange(state); } }),
        input('Fecha de nacimiento', { type: 'date', value: state.birthDate, oninput: (e) => { state.birthDate = e.target.value; onChange(state); } }, 'Opcional. Para recordatorios, edad y contexto.'),
        input('Pronombres (opcional)', { value: state.pronouns, oninput: (e) => { state.pronouns = e.target.value; onChange(state); }, placeholder: 'él/le, ella/la, …' }),
      ]);
      return wrap;
    },
  },

  // ============ PERSONALITY ============
  {
    id: 'personality',
    title: 'Tu personalidad',
    render({ data, onChange }) {
      const state = { ...data };
      state.communicationStyle ??= '';
      state.preferredLanguages ??= ['español'];
      state.interests ??= [];
      state.values ??= [];
      state.bio ??= '';
      onChange(state);

      const wrap = h('div', {}, [
        intro('Estilo y carácter', 'Cómo eres', 'Esto le da contexto a la IA para hablarte como te gusta y entender tus prioridades.'),
        h('div', { class: 'mb-4' }, [
          h('label', { class: 'block text-xs uppercase tracking-widest text-ink-300 mb-2' }, 'Estilo de comunicación preferido'),
          radio([
            { value: 'directo', label: 'Directo', description: 'Al grano, sin rodeos. Como un consultor senior.' },
            { value: 'cercano', label: 'Cercano', description: 'Cálido, con calidez, como un amigo experto.' },
            { value: 'formal', label: 'Formal', description: 'Profesional y estructurado.' },
            { value: 'desafiante', label: 'Desafiante', description: 'Te cuestiona, sparring partner.' },
          ], state.communicationStyle, (v) => { state.communicationStyle = v; onChange(state); }),
        ]),

        h('div', { class: 'mb-4' }, [
          h('label', { class: 'block text-xs uppercase tracking-widest text-ink-300 mb-2' }, 'Idiomas que hablas'),
          chips(['español', 'inglés', 'francés', 'alemán', 'italiano', 'catalán', 'portugués'], state.preferredLanguages, (v) => { state.preferredLanguages = v; onChange(state); }),
        ]),

        h('div', { class: 'mb-4' }, [
          h('label', { class: 'block text-xs uppercase tracking-widest text-ink-300 mb-2' }, 'Intereses / pasiones'),
          chips(
            ['coches', 'inmuebles', 'cripto', 'inversión', 'tecnología', 'IA', 'fitness', 'gastronomía', 'viajes', 'lectura', 'productividad', 'emprendimiento', 'shopify', 'marketing', 'finanzas'],
            state.interests,
            (v) => { state.interests = v; onChange(state); },
          ),
        ]),

        h('div', { class: 'mb-4' }, [
          h('label', { class: 'block text-xs uppercase tracking-widest text-ink-300 mb-2' }, 'Valores que te definen'),
          chips(
            ['libertad', 'familia', 'crecimiento', 'autenticidad', 'eficiencia', 'creatividad', 'disciplina', 'innovación', 'integridad', 'aventura', 'salud', 'aprendizaje'],
            state.values,
            (v) => { state.values = v; onChange(state); },
          ),
        ]),

        textarea('Bio corta sobre ti (opcional)', { value: state.bio, oninput: (e) => { state.bio = e.target.value; onChange(state); }, placeholder: 'Emprendedor en Alicante. Compro y vendo coches, gestiono alquileres, construyo software.' }),
      ]);
      return wrap;
    },
  },

  // ============ MISSION ============
  {
    id: 'mission',
    title: 'Tu misión',
    render({ data, onChange }) {
      const state = { ...data };
      onChange(state);
      return h('div', {}, [
        intro('North Star', '¿Por qué te levantas?', 'CARLOS OS te ayudará mucho mejor si sabe lo que persigues. No tiene que ser perfecto — escribe lo que sientes ahora.'),
        textarea('Tu misión (en 1-2 frases)', {
          value: state.mission ?? '',
          oninput: (e) => { state.mission = e.target.value; onChange(state); },
          placeholder: 'Construir negocios rentables, ser libre con mi tiempo y mi familia.',
        }, '¿Qué quieres conseguir en la vida en lo profesional?'),
        textarea('Tu visión a 5 años', {
          value: state.vision ?? '',
          oninput: (e) => { state.vision = e.target.value; onChange(state); },
          placeholder: 'Autosell escalado, 10 inmuebles funcionando solos, CARLOS.AI con clientes recurrentes, mi familia bien...',
        }),
        textarea('Tu "north star" personal (north star = la estrella que te guía)', {
          value: state.northStar ?? '',
          oninput: (e) => { state.northStar = e.target.value; onChange(state); },
          placeholder: 'Tener libertad económica y de tiempo construyendo cosas que me apasionan.',
        }),
      ]);
    },
  },

  // ============ LIFESTYLE ============
  {
    id: 'lifestyle',
    title: 'Tu día a día',
    render({ data, onChange }) {
      const state = { ...data };
      onChange(state);
      return h('div', {}, [
        intro('Ritmo', 'Cómo es tu día', 'Para que el sistema te proponga acciones cuando estés en mejor momento.'),
        h('div', { class: 'mb-4' }, [
          h('label', { class: 'block text-xs uppercase tracking-widest text-ink-300 mb-2' }, '¿Cuándo trabajas mejor?'),
          radio([
            { value: 'mañana', label: 'Mañanas tempranas', description: '6:00-12:00 más productivo' },
            { value: 'medio-día', label: 'Medio día', description: '10:00-15:00 más productivo' },
            { value: 'tarde', label: 'Tardes', description: '15:00-21:00 más productivo' },
            { value: 'noche', label: 'Noches', description: 'Cuando todo está tranquilo' },
          ], state.workSchedule, (v) => { state.workSchedule = v; onChange(state); }),
        ]),
        input('Horas de sueño objetivo', { type: 'number', step: '0.5', min: '4', max: '12', value: state.sleepHours ?? 7, oninput: (e) => { state.sleepHours = parseFloat(e.target.value); onChange(state); } }),
        textarea('Tu rutina de mañana ideal (opcional)', {
          value: state.morningRoutine ?? '',
          oninput: (e) => { state.morningRoutine = e.target.value; onChange(state); },
          placeholder: 'Café, revisar emails, deporte, revisar pipeline de Autosell, planificar el día...',
        }),
      ]);
    },
  },

  // ============ BUSINESSES ============
  {
    id: 'businesses',
    title: 'Tus negocios',
    render({ data, onChange }) {
      const state = { ...data };
      state.activeBusinesses ??= ['autosell', 'inmuebles'];
      onChange(state);
      return h('div', {}, [
        intro('Frentes activos', 'En qué andas metido', 'Marca todos los negocios que tienes activos. Cada uno tendrá su propio módulo en CARLOS OS.'),
        chips(
          ['autosell', 'inmuebles', 'carlos-ai', 'coco-doree', 'suiza-go', 'consulting', 'inversión', 'otro'],
          state.activeBusinesses,
          (v) => { state.activeBusinesses = v; onChange(state); },
        ),
        h('p', { class: 'mt-4 text-sm text-ink-300' }, 'Si tienes algo no listado, añádelo en los próximos pasos.'),
      ]);
    },
  },

  // ============ AUTOSELL ============
  {
    id: 'autosell',
    title: 'Autosell.es',
    render({ data, onChange }) {
      const state = { ...data };
      state.vehicles ??= [];
      state.businessNotes ??= '';
      onChange(state);

      const list = h('div', { class: 'space-y-3 mb-4' });

      function renderList() {
        list.innerHTML = '';
        state.vehicles.forEach((v, i) => {
          list.appendChild(h('div', { class: 'flex gap-2 items-center bg-base-800 border border-base-600 rounded-lg p-3' }, [
            h('span', { class: 'flex-1' }, `${v.brand} ${v.model} ${v.year ? `(${v.year})` : ''}`),
            h('button', { class: 'text-red-400 text-sm', onclick: () => { state.vehicles.splice(i, 1); onChange(state); renderList(); } }, '✕'),
          ]));
        });
      }

      const brandInput = h('input', { placeholder: 'Marca', class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm flex-1' });
      const modelInput = h('input', { placeholder: 'Modelo', class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm flex-1' });
      const yearInput = h('input', { placeholder: 'Año', type: 'number', class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm w-24' });
      const addBtn = h('button', {
        type: 'button',
        class: 'bg-gold-400/20 text-gold-300 border border-gold-400/50 rounded-lg px-3 py-2 text-sm',
        onclick: () => {
          if (!brandInput.value || !modelInput.value) return;
          state.vehicles.push({ brand: brandInput.value, model: modelInput.value, year: yearInput.value ? Number(yearInput.value) : null });
          brandInput.value = ''; modelInput.value = ''; yearInput.value = '';
          onChange(state);
          renderList();
        },
      }, '+ Añadir');

      renderList();

      return h('div', {}, [
        intro('Autosell', '¿Algún coche en pipeline ahora?', 'Añade los vehículos que tienes en este momento (opcional). Luego añadirás más desde el panel.'),
        h('div', { class: 'flex gap-2 mb-4' }, [brandInput, modelInput, yearInput, addBtn]),
        list,
        textarea('Notas generales sobre Autosell (opcional)', {
          value: state.businessNotes,
          oninput: (e) => { state.businessNotes = e.target.value; onChange(state); },
          placeholder: 'Modelo de negocio, márgenes típicos, proveedores habituales...',
        }),
      ]);
    },
  },

  // ============ PROPERTIES ============
  {
    id: 'properties',
    title: 'Tus inmuebles',
    render({ data, onChange }) {
      const state = { ...data };
      state.properties ??= [];
      onChange(state);

      const list = h('div', { class: 'space-y-3 mb-4' });

      function renderList() {
        list.innerHTML = '';
        state.properties.forEach((p, i) => {
          list.appendChild(h('div', { class: 'bg-base-800 border border-base-600 rounded-lg p-3 flex items-center gap-2' }, [
            h('span', { class: 'flex-1' }, `${p.name} — ${p.city || 'Alicante'}`),
            h('button', { class: 'text-red-400 text-sm', onclick: () => { state.properties.splice(i, 1); onChange(state); renderList(); } }, '✕'),
          ]));
        });
      }

      const nameI = h('input', { placeholder: 'Nombre (ej. Piso Calle Mayor)', class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm flex-1' });
      const addrI = h('input', { placeholder: 'Dirección', class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm flex-1' });
      const opModeI = h('select', { class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm' }, [
        h('option', { value: 'short_term' }, 'Corto plazo (Airbnb)'),
        h('option', { value: 'long_term' }, 'Largo plazo (alquiler)'),
        h('option', { value: 'hybrid' }, 'Mixto'),
        h('option', { value: 'own_use' }, 'Uso propio'),
      ]);
      const valI = h('input', { type: 'number', placeholder: 'Valor €', class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm w-32' });
      const icalI = h('input', { placeholder: 'URL iCal Airbnb (opcional)', class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm flex-1' });
      const addBtn = h('button', {
        type: 'button',
        class: 'bg-gold-400/20 text-gold-300 border border-gold-400/50 rounded-lg px-3 py-2 text-sm',
        onclick: () => {
          if (!nameI.value) return;
          const slug = nameI.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
          state.properties.push({
            name: nameI.value,
            slug,
            address: addrI.value || null,
            city: 'Alicante',
            operationMode: opModeI.value,
            currentValuation: valI.value ? Number(valI.value) : null,
            icalUrl: icalI.value || null,
          });
          nameI.value = ''; addrI.value = ''; valI.value = ''; icalI.value = '';
          onChange(state);
          renderList();
        },
      }, '+ Añadir');

      renderList();

      return h('div', {}, [
        intro('Inmuebles', 'Tus alquileres turísticos', 'Añade cada inmueble. El iCal de Airbnb (en Disponibilidad → Sincronizar calendarios) sirve para sync automático.'),
        h('div', { class: 'space-y-2 mb-4' }, [
          h('div', { class: 'flex gap-2' }, [nameI, addrI]),
          h('div', { class: 'flex gap-2' }, [opModeI, valI, addBtn]),
          h('div', { class: 'flex gap-2' }, [icalI]),
        ]),
        list,
      ]);
    },
  },

  // ============ SHOPIFY ============
  {
    id: 'shopify',
    title: 'Shopify',
    render({ data, onChange }) {
      const state = { ...data };
      state.shopifyStores ??= [];
      onChange(state);

      const list = h('div', { class: 'space-y-3 mb-4' });
      function renderList() {
        list.innerHTML = '';
        state.shopifyStores.forEach((s, i) => {
          list.appendChild(h('div', { class: 'bg-base-800 border border-base-600 rounded-lg p-3 flex items-center gap-2' }, [
            h('span', { class: 'flex-1' }, `${s.label || s.store}`),
            h('button', { class: 'text-red-400 text-sm', onclick: () => { state.shopifyStores.splice(i, 1); onChange(state); renderList(); } }, '✕'),
          ]));
        });
      }
      const storeI = h('input', { placeholder: 'mi-tienda.myshopify.com o suiza-go.com', class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm flex-1' });
      const labelI = h('input', { placeholder: 'Etiqueta (Suiza-Go, COCO/DORÉE…)', class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm flex-1' });
      const tokenI = h('input', { type: 'password', placeholder: 'Admin token (opcional, shpat_…)', class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm flex-1' });
      const addBtn = h('button', {
        type: 'button',
        class: 'bg-gold-400/20 text-gold-300 border border-gold-400/50 rounded-lg px-3 py-2 text-sm',
        onclick: () => {
          if (!storeI.value) return;
          state.shopifyStores.push({ store: storeI.value, label: labelI.value || null, adminToken: tokenI.value || null });
          storeI.value = ''; labelI.value = ''; tokenI.value = '';
          onChange(state);
          renderList();
        },
      }, '+ Añadir');
      renderList();

      return h('div', {}, [
        intro('Shopify', 'Tiendas online', 'Si tienes Shopify (Suiza-Go, COCO/DORÉE…), añádelas aquí. El token es opcional, solo necesario si quieres que CARLOS OS lea pedidos.'),
        h('div', { class: 'space-y-2 mb-4' }, [
          h('div', { class: 'flex gap-2' }, [storeI, labelI]),
          h('div', { class: 'flex gap-2' }, [tokenI, addBtn]),
        ]),
        list,
        h('p', { class: 'text-xs text-ink-400' }, '🔒 El token se encripta AES-256-GCM en BD.'),
      ]);
    },
  },

  // ============ FINANCES ============
  {
    id: 'finances',
    title: 'Finanzas personales',
    render({ data, onChange }) {
      const state = { ...data };
      onChange(state);
      return h('div', {}, [
        intro('Patrón mensual', 'Tu pulso financiero', 'Describe en pocas líneas tu situación. La IA usará esto sin compartirlo fuera.'),
        textarea('¿Cómo es tu mes típico en ingresos y gastos?', {
          value: state.monthlyExpensesNote ?? '',
          oninput: (e) => { state.monthlyExpensesNote = e.target.value; onChange(state); },
          placeholder: 'Autosell me da X-Y al mes. Inmuebles otros Z. Gastos fijos (vivienda, suscripciones, etc) suman W. Ahorro entre A y B.',
        }),
        h('p', { class: 'text-xs text-ink-400' }, '🔒 Es solo para tu IA personal. Puedes ser tan detallado o vago como quieras.'),
      ]);
    },
  },

  // ============ DEBTS ============
  {
    id: 'debts',
    title: 'Deudas y préstamos',
    render({ data, onChange }) {
      const state = { ...data };
      state.debts ??= [];
      onChange(state);

      const list = h('div', { class: 'space-y-3 mb-4' });
      function renderList() {
        list.innerHTML = '';
        state.debts.forEach((d, i) => {
          list.appendChild(h('div', { class: 'bg-base-800 border border-base-600 rounded-lg p-3 flex items-center gap-2' }, [
            h('span', { class: 'flex-1' }, `${d.name} — ${d.outstandingBalance || d.principalAmount}€ @ ${d.interestRate || '?'}%`),
            h('button', { class: 'text-red-400 text-sm', onclick: () => { state.debts.splice(i, 1); onChange(state); renderList(); } }, '✕'),
          ]));
        });
      }
      const nameI = h('input', { placeholder: 'Nombre (ej. Hipoteca piso Tabarca / Nota Pibank)', class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm flex-1' });
      const typeI = h('select', { class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm' }, [
        h('option', { value: 'mortgage' }, 'Hipoteca'),
        h('option', { value: 'personal' }, 'Préstamo personal'),
        h('option', { value: 'business' }, 'Préstamo empresarial'),
        h('option', { value: 'credit_card' }, 'Tarjeta crédito'),
      ]);
      const principalI = h('input', { type: 'number', placeholder: 'Capital inicial €', class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm w-40' });
      const outstandingI = h('input', { type: 'number', placeholder: 'Pendiente €', class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm w-40' });
      const rateI = h('input', { type: 'number', step: '0.01', placeholder: '% anual', class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm w-32' });
      const monthlyI = h('input', { type: 'number', placeholder: 'Cuota mensual €', class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm w-40' });
      const addBtn = h('button', {
        type: 'button',
        class: 'bg-gold-400/20 text-gold-300 border border-gold-400/50 rounded-lg px-3 py-2 text-sm',
        onclick: () => {
          if (!nameI.value || !principalI.value) return;
          state.debts.push({
            name: nameI.value,
            type: typeI.value,
            principalAmount: Number(principalI.value),
            outstandingBalance: outstandingI.value ? Number(outstandingI.value) : null,
            interestRate: rateI.value ? Number(rateI.value) : null,
            monthlyPayment: monthlyI.value ? Number(monthlyI.value) : null,
          });
          nameI.value = ''; principalI.value = ''; outstandingI.value = ''; rateI.value = ''; monthlyI.value = '';
          onChange(state);
          renderList();
        },
      }, '+ Añadir');
      renderList();

      return h('div', {}, [
        intro('Deudas activas', 'Préstamos e hipotecas', 'Para calcular tu patrimonio neto. Recuerda: la nota simple del Pibank cuenta aquí.'),
        h('div', { class: 'space-y-2 mb-4' }, [
          h('div', { class: 'flex gap-2' }, [nameI, typeI]),
          h('div', { class: 'flex gap-2' }, [principalI, outstandingI, rateI, monthlyI]),
          addBtn,
        ]),
        list,
      ]);
    },
  },

  // ============ INVESTMENTS ============
  {
    id: 'investments',
    title: 'Inversiones',
    render({ data, onChange }) {
      const state = { ...data };
      state.stocks ??= [];
      onChange(state);

      const list = h('div', { class: 'space-y-3 mb-4' });
      function renderList() {
        list.innerHTML = '';
        state.stocks.forEach((s, i) => {
          list.appendChild(h('div', { class: 'bg-base-800 border border-base-600 rounded-lg p-3 flex items-center gap-2' }, [
            h('span', { class: 'flex-1' }, `${s.ticker} — ${s.shares} acc @ ${s.avgPrice}`),
            h('button', { class: 'text-red-400 text-sm', onclick: () => { state.stocks.splice(i, 1); onChange(state); renderList(); } }, '✕'),
          ]));
        });
      }
      const tickerI = h('input', { placeholder: 'Ticker (AAPL, ITX.MC, …)', class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm flex-1' });
      const sharesI = h('input', { type: 'number', step: '0.001', placeholder: 'Nº acciones', class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm w-40' });
      const priceI = h('input', { type: 'number', step: '0.01', placeholder: 'Precio medio', class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm w-40' });
      const addBtn = h('button', {
        type: 'button',
        class: 'bg-gold-400/20 text-gold-300 border border-gold-400/50 rounded-lg px-3 py-2 text-sm',
        onclick: () => {
          if (!tickerI.value || !sharesI.value || !priceI.value) return;
          state.stocks.push({ ticker: tickerI.value.toUpperCase(), shares: Number(sharesI.value), avgPrice: Number(priceI.value) });
          tickerI.value = ''; sharesI.value = ''; priceI.value = '';
          onChange(state);
          renderList();
        },
      }, '+ Añadir');
      renderList();

      return h('div', {}, [
        intro('Inversiones', 'Acciones que tienes', 'Añade tus posiciones manualmente. Cripto vendrá conectado a KuCoin en Fase 2.'),
        h('div', { class: 'flex gap-2 mb-4' }, [tickerI, sharesI, priceI, addBtn]),
        list,
        h('p', { class: 'text-xs text-ink-400' }, 'Los precios se actualizan automáticamente con Yahoo Finance (gratis, sin keys).'),
      ]);
    },
  },

  // ============ GOALS ============
  {
    id: 'goals',
    title: 'Objetivos',
    render({ data, onChange }) {
      const state = { ...data };
      state.goals ??= [];
      onChange(state);

      const list = h('div', { class: 'space-y-3 mb-4' });
      function renderList() {
        list.innerHTML = '';
        state.goals.forEach((g, i) => {
          list.appendChild(h('div', { class: 'bg-base-800 border border-base-600 rounded-lg p-3 flex items-center gap-2' }, [
            h('span', { class: 'flex-1' }, `${g.title} — ${g.targetValue || '?'} ${g.targetUnit || ''}`),
            h('button', { class: 'text-red-400 text-sm', onclick: () => { state.goals.splice(i, 1); onChange(state); renderList(); } }, '✕'),
          ]));
        });
      }
      const titleI = h('input', { placeholder: 'Título (ej. 100k facturación Autosell 2026)', class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm flex-1' });
      const catI = h('select', { class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm' }, [
        h('option', { value: 'financiero' }, 'Financiero'),
        h('option', { value: 'salud' }, 'Salud'),
        h('option', { value: 'negocio' }, 'Negocio'),
        h('option', { value: 'personal' }, 'Personal'),
      ]);
      const valueI = h('input', { type: 'number', placeholder: 'Objetivo', class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm w-32' });
      const unitI = h('input', { placeholder: 'Unidad (€, kg, h…)', class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm w-32' });
      const dateI = h('input', { type: 'date', class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm' });
      const addBtn = h('button', {
        type: 'button',
        class: 'bg-gold-400/20 text-gold-300 border border-gold-400/50 rounded-lg px-3 py-2 text-sm',
        onclick: () => {
          if (!titleI.value) return;
          state.goals.push({
            title: titleI.value,
            category: catI.value,
            targetValue: valueI.value ? Number(valueI.value) : null,
            targetUnit: unitI.value || null,
            targetDate: dateI.value ? new Date(dateI.value).getTime() : null,
          });
          titleI.value = ''; valueI.value = ''; unitI.value = ''; dateI.value = '';
          onChange(state);
          renderList();
        },
      }, '+ Añadir');
      renderList();

      return h('div', {}, [
        intro('Objetivos', 'A dónde quieres llegar', 'Define tus objetivos para 3-12 meses. CARLOS OS te recordará progresarlos.'),
        h('div', { class: 'space-y-2 mb-4' }, [
          h('div', { class: 'flex gap-2' }, [titleI, catI]),
          h('div', { class: 'flex gap-2' }, [valueI, unitI, dateI, addBtn]),
        ]),
        list,
      ]);
    },
  },

  // ============ HEALTH ============
  {
    id: 'health',
    title: 'Salud y hábitos',
    render({ data, onChange }) {
      const state = { ...data };
      state.habits ??= [
        { name: 'Deporte', emoji: '💪', targetFrequency: 'daily', targetCount: 1 },
        { name: 'Leer', emoji: '📚', targetFrequency: 'daily', targetCount: 1 },
        { name: 'Meditar', emoji: '🧘', targetFrequency: 'daily', targetCount: 1 },
      ];
      onChange(state);

      const list = h('div', { class: 'space-y-2 mb-4' });
      function renderList() {
        list.innerHTML = '';
        state.habits.forEach((hb, i) => {
          list.appendChild(h('div', { class: 'bg-base-800 border border-base-600 rounded-lg p-3 flex items-center gap-2' }, [
            h('span', { class: 'flex-1' }, `${hb.emoji} ${hb.name} — ${hb.targetFrequency}`),
            h('button', { class: 'text-red-400 text-sm', onclick: () => { state.habits.splice(i, 1); onChange(state); renderList(); } }, '✕'),
          ]));
        });
      }
      const emojiI = h('input', { placeholder: '🔥', maxlength: 4, class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm w-20 text-center' });
      const nameI = h('input', { placeholder: 'Nombre hábito', class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm flex-1' });
      const freqI = h('select', { class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm' }, [
        h('option', { value: 'daily' }, 'Diario'),
        h('option', { value: 'weekly' }, 'Semanal'),
        h('option', { value: 'x-per-week' }, 'X veces / semana'),
      ]);
      const addBtn = h('button', {
        type: 'button',
        class: 'bg-gold-400/20 text-gold-300 border border-gold-400/50 rounded-lg px-3 py-2 text-sm',
        onclick: () => {
          if (!nameI.value) return;
          state.habits.push({ emoji: emojiI.value || '•', name: nameI.value, targetFrequency: freqI.value });
          emojiI.value = ''; nameI.value = '';
          onChange(state);
          renderList();
        },
      }, '+');
      renderList();

      return h('div', {}, [
        intro('Salud y hábitos', 'Tu rutina', 'Hábitos a trackear cada día. Te he pre-llenado con algunos comunes — ajusta a tu gusto.'),
        h('div', { class: 'flex gap-2 mb-4' }, [emojiI, nameI, freqI, addBtn]),
        list,
      ]);
    },
  },

  // ============ AI PREFERENCES ============
  {
    id: 'ai_preferences',
    title: 'Tu IA personal',
    render({ data, onChange }) {
      const state = { ...data };
      state.aiTone ??= 'directo';
      state.aiUsageContexts ??= [];
      state.aiSensitiveTopics ??= [];
      onChange(state);

      return h('div', {}, [
        intro('IA a tu medida', 'Cómo quieres que te hable', 'Esto entrena el contexto base de Claude/GPT y OpenClaw.'),

        h('div', { class: 'mb-4' }, [
          h('label', { class: 'block text-xs uppercase tracking-widest text-ink-300 mb-2' }, 'Tono'),
          radio([
            { value: 'directo', label: '🎯 Directo', description: 'Respuestas concisas, accionables' },
            { value: 'empático', label: '🤝 Empático', description: 'Comprensivo, con calidez' },
            { value: 'desafiante', label: '⚔️ Desafiante', description: 'Sparring partner, te cuestiona' },
            { value: 'detallado', label: '📚 Detallado', description: 'Profundo, explicativo' },
          ], state.aiTone, (v) => { state.aiTone = v; onChange(state); }),
        ]),

        h('div', { class: 'mb-4' }, [
          h('label', { class: 'block text-xs uppercase tracking-widest text-ink-300 mb-2' }, 'En qué contextos vas a usar la IA'),
          chips(
            ['planificación', 'finanzas', 'redacción anuncios', 'análisis inmuebles', 'análisis vehículos', 'salud / fitness', 'productividad', 'programación', 'shopping', 'idiomas', 'investigación', 'creatividad'],
            state.aiUsageContexts,
            (v) => { state.aiUsageContexts = v; onChange(state); },
          ),
        ]),

        h('div', { class: 'mb-4' }, [
          h('label', { class: 'block text-xs uppercase tracking-widest text-ink-300 mb-2' }, 'Temas sensibles (van solo a IA local, nunca cloud)'),
          chips(
            ['saldos bancarios', 'datos KuCoin', 'direcciones exactas', 'matrículas vehículos', 'datos clientes', 'contraseñas', 'salud personal'],
            state.aiSensitiveTopics,
            (v) => { state.aiSensitiveTopics = v; onChange(state); },
          ),
        ]),
      ]);
    },
  },

  // ============ CHANNELS ============
  {
    id: 'channels',
    title: 'Canales móviles',
    render({ data, onChange }) {
      const state = { ...data };
      onChange(state);
      return h('div', {}, [
        intro('Móvil', 'Habla con CARLOS OS desde tu teléfono', 'OpenClaw conecta a Telegram, WhatsApp, iMessage, Signal y más.'),

        h('div', { class: 'bg-base-800 border border-emerald-400/30 rounded-xl p-5 mb-4' }, [
          h('div', { class: 'flex items-center gap-2 mb-2' }, [
            h('span', { class: 'text-emerald-400' }, '✓'),
            h('h4', { class: 'font-semibold' }, 'Telegram ya configurado'),
          ]),
          h('p', { class: 'text-sm text-ink-300' }, 'El bot está activo en tu VPS. Busca tu bot en Telegram (el username está en BotFather) y envíale /start.'),
        ]),

        input('Username de tu bot Telegram (opcional)', {
          value: state.telegramBotUsername ?? '',
          oninput: (e) => { state.telegramBotUsername = e.target.value; onChange(state); },
          placeholder: '@carlos_os_bot',
        }, 'Lo encuentras en BotFather al crear el bot. Es solo informativo.'),

        h('p', { class: 'text-sm text-ink-300 mt-4' }, 'WhatsApp e iMessage se configurarán cuando el sistema esté estable (requieren pasos adicionales).'),
      ]);
    },
  },

  // ============ FINISH ============
  {
    id: 'finish',
    title: '¡Bienvenido!',
    render({ user }) {
      return h('div', { class: 'text-center py-12' }, [
        h('div', { class: 'text-7xl mb-6' }, '🎉'),
        h('h2', { class: 'font-display text-4xl md:text-5xl font-bold mb-4' }, `¡Listo, ${user?.nickname || 'Carlos'}!`),
        h('p', { class: 'text-ink-200 text-lg mb-6 max-w-xl mx-auto' },
          'CARLOS OS te conoce. Tus datos están guardados en tu servidor privado, encriptados, y accesibles solo para ti.'),
        h('div', { class: 'grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto text-left mt-8' }, [
          card('☀️ Cada mañana', 'Ve a /morning para tu briefing diario.'),
          card('💬 Chat IA', 'Habla con Claude desde el panel o por Telegram.'),
          card('📊 Patrimonio', 'Tus inversiones, deudas y objetivos en un sitio.'),
        ]),
        h('p', { class: 'text-sm text-ink-400 mt-8' }, 'Pulsa "Terminar 🎉" para entrar al panel.'),
      ]);
    },
  },
];

function card(title, text) {
  return h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4' }, [
    h('h4', { class: 'font-semibold mb-1' }, title),
    h('p', { class: 'text-sm text-ink-300' }, text),
  ]);
}
